import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatCurrency } from "../lib/formatters";
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  Building2,
  ChevronDown,
  ChevronRight,
  MapPinOff,
  CalendarOff,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useShowOffice } from "../hooks/useShowOffice";
const STATUS_ORDER = ["active", "precon", "bidding", "bid_complete", "complete", "not_won", "not_bid", "archive"];
const STATUS_LABELS = {
  active: "Active",
  precon: "Precon",
  bidding: "Bidding",
  bid_complete: "Bid Complete",
  complete: "Complete",
  not_won: "Not Won",
  not_bid: "Not Bid",
  archive: "Archive",
};

const JobsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});
  
  const statusFromUrl = searchParams.get("status") || "all";
  const [statusFilter, setStatusFilter] = useState(statusFromUrl);
  const { showOffice, toggleShowOffice } = useShowOffice();

  useEffect(() => {
    const status = searchParams.get("status") || "all";
    setStatusFilter(status);
  }, [searchParams]);

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    if (newStatus === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", newStatus);
    }
    setSearchParams(searchParams);
  };

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showOffice) params.append("include_office", "true");
      const response = await apiClient.get(`/jobs?${params}`);
      setJobs(response.data);
    } catch (error) {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, showOffice]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const getStatusBadge = (status) => {
    const variants = {
      bidding: "badge-bidding",
      precon: "badge-precon",
      active: "badge-active",
      complete: "badge-complete",
      archive: "badge-archive",
      not_bid: "badge-archive",
      bid_complete: "badge-bid-complete",
      not_won: "badge-archive",
    };
    return (
      <span className={`badge-status ${variants[status] || "badge-precon"}`}>
        {STATUS_LABELS[status] || status}
      </span>
    );
  };

  const archivedStatuses = ["not_won", "not_bid", "archive"];

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.gc_name?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus;
    if (statusFilter === "all") {
      matchesStatus = !archivedStatuses.includes(job.status);
    } else if (statusFilter === "archived") {
      matchesStatus = archivedStatuses.includes(job.status);
    } else {
      matchesStatus = job.status === statusFilter;
    }

    return matchesSearch && matchesStatus;
  });

  // Group jobs by status, sorted alphabetically within each group
  const groupedJobs = useMemo(() => {
    const groups = {};
    for (const job of filteredJobs) {
      const status = job.status || "unknown";
      if (!groups[status]) groups[status] = [];
      groups[status].push(job);
    }
    // Sort each group alphabetically
    for (const status in groups) {
      groups[status].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" })
      );
    }
    return groups;
  }, [filteredJobs]);

  // Order the status sections
  const orderedStatuses = STATUS_ORDER.filter((s) => groupedJobs[s]?.length > 0);

  const toggleSection = (status) => {
    setCollapsedSections((prev) => ({ ...prev, [status]: !prev[status] }));
  };

  // Use backend-calculated totals (includes bid sheet grand_total when available)
  const getEstimate = (job) => job.estimate_total || 0;
  const getActual = (job) => job.actual_total || 0;

  const PROFIT_LOSS_STATUSES = ["active", "complete", "archive"];
  const showProfitLoss = (status) => PROFIT_LOSS_STATUSES.includes(status);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-6" data-testid="jobs-page">
      {/* Page Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-4xl font-bold tracking-tight text-foreground shrink-0">Jobs</h1>
          <Link to="/jobs/new" className="sm:hidden" aria-label="Create Job">
            <Button size="sm" className="h-8 px-2.5 text-xs" data-testid="create-job-btn-mobile">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </Link>
        </div>
        <Link to="/jobs/new" className="hidden sm:inline-flex">
          <Button data-testid="create-job-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Job
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-jobs-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="bidding">Bidding</SelectItem>
            <SelectItem value="bid_complete">Bid Complete</SelectItem>
            <SelectItem value="precon">Precon</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <Label htmlFor="show-office" className="text-sm font-medium cursor-pointer whitespace-nowrap">
            Show Office
          </Label>
          <Switch
            id="show-office"
            checked={showOffice}
            onCheckedChange={toggleShowOffice}
            data-testid="show-office-toggle"
          />
        </div>
      </div>

      {/* Jobs Table grouped by status */}
      {filteredJobs.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No jobs found</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "Get started by creating your first job"}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <Link to="/jobs/new">
                <Button data-testid="empty-create-job-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Job
                </Button>
              </Link>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {orderedStatuses.map((status) => (
            <Card key={status} className="overflow-hidden" data-testid={`jobs-group-${status}`}>
              {/* Section Header */}
              <button
                onClick={() => toggleSection(status)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/60 hover:bg-muted/80 transition-colors"
                data-testid={`toggle-${status}`}
              >
                <div className="flex items-center gap-3">
                  {collapsedSections[status] ? (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                  {getStatusBadge(status)}
                  <span className="text-sm text-muted-foreground">
                    ({groupedJobs[status].length})
                  </span>
                </div>
              </button>

              {/* Table */}
              {!collapsedSections[status] && (
                <Table>
                  <colgroup>
                    <col className="w-[28%]" />
                    <col className="w-[30%]" />
                    <col className="w-[14%]" />
                    <col className="w-[14%]" />
                    <col className="w-[14%]" />
                  </colgroup>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="font-semibold text-sm">Job Name</TableHead>
                      <TableHead className="font-semibold text-sm">GC</TableHead>
                      <TableHead className="text-right font-semibold text-sm">Estimate</TableHead>
                      <TableHead className="text-right font-semibold text-sm">
                        {showProfitLoss(status) ? "Actual" : ""}
                      </TableHead>
                      <TableHead className="text-right font-semibold text-sm">
                        {showProfitLoss(status) ? "Profit/Loss" : "Bid Amount"}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedJobs[status].map((job) => (
                      <TableRow
                        key={job.id}
                        className="cursor-pointer hover:bg-muted/40 transition-colors"
                        data-testid={`job-row-${job.id}`}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                      >
                        <TableCell className="font-medium text-sm">
                          {job.name}
                          {job.status === "active" && !job.is_system_job && !(job.street || "").trim() && !(job.address || "").trim() && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 align-middle rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-medium"
                              title="No street address — foreman location matching won't work for this job"
                              data-testid={`no-address-badge-${job.id}`}
                            >
                              <MapPinOff className="w-3 h-3" /> No address
                            </span>
                          )}
                          {job.contract_received_date && !job.is_system_job && !["archive", "not_bid", "bid_complete", "not_won"].includes(job.status) && !job.billing_date && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 align-middle rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-medium"
                              title="Contract received but no billing day set — this job won't appear in Billings Due reminders with a date"
                              data-testid={`no-billing-day-badge-${job.id}`}
                            >
                              <CalendarOff className="w-3 h-3" /> No billing day
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-0">
                          {(() => {
                            const gc = (job.bidding_gcs || []).length > 1 && (job.status === "bidding" || job.status === "bid_complete")
                              ? `${job.bidding_gcs.length} GCs`
                              : (job.gc_name || job.client_name || "-");
                            return <span className="block truncate" title={gc}>{gc}</span>;
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(getEstimate(job))}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {showProfitLoss(status) ? formatCurrency(getActual(job)) : ""}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {showProfitLoss(status) ? (
                            <span
                              className={`font-semibold ${
                                (job.profit_loss || 0) >= 0 ? "profit-positive" : "profit-negative"
                              }`}
                            >
                              {formatCurrency(job.profit_loss)}
                            </span>
                          ) : (
                            <span className="font-semibold">
                              {formatCurrency(getEstimate(job))}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Totals row - always visible even when collapsed */}
              {(status === "active" || status === "complete") && groupedJobs[status].length > 0 && (() => {
                const totalEst = groupedJobs[status].reduce((s, j) => s + getEstimate(j), 0);
                const totalAct = groupedJobs[status].reduce((s, j) => s + getActual(j), 0);
                const totalPL = groupedJobs[status].reduce((s, j) => s + (j.profit_loss || 0), 0);
                return (
                  <Table>
                    <colgroup>
                      <col className="w-[28%]" />
                      <col className="w-[30%]" />
                      <col className="w-[14%]" />
                      <col className="w-[14%]" />
                      <col className="w-[14%]" />
                    </colgroup>
                    <TableBody>
                      <TableRow className="bg-muted/50 border-t-2" data-testid={`${status}-jobs-totals`}>
                        <TableCell className="font-bold text-sm">Totals</TableCell>
                        <TableCell />
                        <TableCell className="text-right font-mono text-sm font-bold">{formatCurrency(totalEst)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold">{formatCurrency(totalAct)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold">
                          <span className={totalPL >= 0 ? "profit-positive" : "profit-negative"}>
                            {formatCurrency(totalPL)}
                          </span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              })()}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsPage;
